import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Calendar } from "lucide-react";
import { Link } from "react-router-dom";

import { formatDisplayDate } from "@/lib/dateFormat";
import { getTaxLabelKey } from "@/lib/fiscalAgendaDisplay";
import {
  describeObligation,
  pickNextObligation,
} from "@/pages/dashboard/nextObligation";
import { getFiscalAgenda } from "@/services/fiscalService";

interface DashboardNextObligationProps {
  /** Free tenants get no fiscal agenda — the card hides entirely. */
  showPaidFiscalDashboard: boolean;
}

/**
 * Single "próximo vencimiento" card for the simple view, mirroring the mobile
 * Inicio screen's AgendaCard.
 *
 * Reads the agenda directly rather than going through
 * `useUpcomingObligationsTimeline`: that hook filters to `dueDate >= today`,
 * so an overdue obligation never reaches its output and this card would show
 * "sin vencimientos próximos" to exactly the user who has one outstanding.
 *
 * Deliberately not a mark-as-paid surface — that stays on the advanced view's
 * DashboardHeaderSummary and the estado-fiscal page.
 */
export function DashboardNextObligation({
  showPaidFiscalDashboard,
}: DashboardNextObligationProps): JSX.Element | null {
  const { t, i18n } = useTranslation();

  // Same query key as DashboardHeaderSummary so React Query dedupes rather
  // than issuing a second request for the same year.
  const year = new Date().getFullYear();
  const { data: agendaGrouped } = useQuery({
    queryKey: ["fiscal-agenda", year + "-01", year + "-12"],
    queryFn: () => getFiscalAgenda(year + "-01", year + "-12"),
    enabled: showPaidFiscalDashboard,
  });

  const obligation = useMemo(
    () => pickNextObligation(agendaGrouped),
    [agendaGrouped],
  );

  if (!showPaidFiscalDashboard) return null;

  const detail = obligation ? describeObligation(obligation) : null;

  return (
    <Link to="/estado-fiscal" className="fx-next-oblig">
      <div className="fx-next-oblig-head">
        <div className="fx-next-oblig-ico">
          <Calendar aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <div className="fx-next-oblig-title">
            {t("dashboard.nextObligation.title", "Agenda fiscal")}
          </div>
          <div className="fx-next-oblig-sub">
            {detail
              ? detail.isOverdue
                ? t("dashboard.nextObligation.overdue", "Vencimiento atrasado")
                : t("dashboard.nextObligation.upcoming", "Próximo vencimiento")
              : t("dashboard.nextObligation.none", "Sin vencimientos próximos")}
          </div>
        </div>
      </div>

      {detail ? (
        <div className="fx-next-oblig-detail">
          <span>
            <span
              className={`fx-next-oblig-eyebrow${detail.isOverdue ? " is-overdue" : ""}`}
            >
              {detail.isOverdue
                ? t("dashboard.nextObligation.badgeOverdue", "Vencida")
                : t("dashboard.nextObligation.badgeUpcoming", "Próximo")}
            </span>
            <span className="fx-next-oblig-label">
              {t(getTaxLabelKey(obligation!.taxType), obligation!.taxType)} ·{" "}
              {formatDisplayDate(obligation!.dueDate, {
                locale: i18n.language,
                useCalendarDate: true,
              })}
            </span>
          </span>
          <span
            className={`fx-next-oblig-rel${detail.isOverdue ? " is-overdue" : ""}`}
          >
            {detail.days < 0
              ? detail.days === -1
                ? t("dashboard.headerSummary.dueOverdueDay")
                : t("dashboard.headerSummary.dueOverdueDays", {
                    count: Math.abs(detail.days),
                  })
              : detail.days === 0
                ? t("dashboard.headerSummary.dueToday")
                : detail.days === 1
                  ? t("dashboard.headerSummary.dueTomorrow")
                  : t("dashboard.headerSummary.dueInDays", {
                      count: detail.days,
                    })}
          </span>
        </div>
      ) : null}
    </Link>
  );
}
